import { findTemplate, SHAPE_TEMPLATES, THEME_TEMPLATES } from '@aquilixshop/cod-config';
import { LTR_ISOLATE_CSS } from '../_shared/ltrIsolate.js';
import { resolveColor } from '../../theme/colorTokens.js';
/** The input STYLE variants — the keystone control (no COD app has this). `outlined` is the current look. */
export const CF_VARIANTS = ['outlined', 'filled', 'underlined', 'soft', 'glass'];
/** Named box-shadow ramp for the `Elevation` select (kept out of free-form so it never renders garish). `soft`
 * is the validated Claude-Design card shadow (the out-of-the-box look — see `formShadow` default below). */
const SHADOW = {
    none: 'none',
    soft: '0 1px 2px rgba(0,0,0,.04), 0 12px 32px -16px rgba(0,0,0,.1)',
    medium: '0 2px 4px rgba(0,0,0,.06), 0 18px 40px -16px rgba(0,0,0,.24)',
    floating: '0 10px 44px -10px rgba(0,0,0,.30)',
};
/** Colour token → the `--cf-*` var it drives (used to expand a Palette preset's patch). */
const COLOR_VAR = {
    bg: '--cf-bg',
    surface: '--cf-surface',
    soft: '--cf-soft',
    text: '--cf-text',
    muted: '--cf-muted',
    border: '--cf-border',
    accent: '--cf-accent',
    accentTint: '--cf-accent-tint',
    buttonBg: '--cf-btn-bg',
    buttonText: '--cf-btn-text',
};
/** Radius token → var (used to expand a Corner-shape preset's patch). */
const RADIUS_VAR = {
    radius: '--cf-radius',
    inputRadius: '--cf-input-radius',
    buttonRadius: '--cf-btn-radius',
};
/**
 * The scoped stylesheet for one cod-form instance (keyed on its `aqx-cf-<id>` class). Every value reads a
 * `--cf-*` var with the CURRENT hardcoded literal as fallback, so with no overrides the output is identical
 * to the pre-0026-05 inline styling. Moving the styling here (from inline `style=`) is what lets us express
 * `::placeholder`, `:focus`, and the structural input variants — none of which an inline style can do. All
 * variant rules are emitted; the wrapper's `data-cf-input` attribute selects which apply (deterministic).
 */
export function codFormCss(cls) {
    const s = `.${cls}`;
    const inp = `${s} .cf-input`;
    // ── theme-token expressions: the validated Claude-Design `--om-*` mapped to our `--cf-*` (design value as
    // fallback), so the unified card inherits the MERCHANT's COD theme yet is pixel-faithful out of the box. ──
    const TXT = 'var(--cf-text,#1b1b1e)';
    const MUT = 'var(--cf-muted,#6e6e77)';
    const BOR = 'var(--cf-border,#e4e4e9)';
    const ACC = 'var(--cf-accent,#0e7a5f)';
    const SOFT = 'var(--cf-accent-tint,var(--cf-soft,#eef7f3))';
    const FIELD = 'var(--cf-input-bg,var(--cf-surface,#ffffff))';
    // A subtle fill for the summary card / phone-dial box / dropdown hover. Derived (a 5% ink tint over the card)
    // instead of `--cf-surface`, because most themes set `--cf-surface` == the card bg (#fff) → it would render
    // invisible; this stays distinct AND theme-aware (adapts to a dark card too).
    const SURF = 'color-mix(in srgb, var(--cf-text,#1b1b1e) 5%, var(--cf-form-bg,var(--cf-bg,#ffffff)))';
    const ERR = 'var(--aqx-theme-danger,#d64545)';
    const RAD = 'var(--cf-radius,12px)';
    const IRAD = 'var(--cf-input-radius,12px)';
    const IH = 'var(--cf-input-height,44px)';
    const CARD_BG = 'var(--cf-form-bg,var(--cf-bg,#ffffff))';
    // The form's horizontal breathing room, in ONE place. It used to be hard-coded as 20px (desktop) / 14px
    // (mobile) on every band — head, fields, summary, promo, actions — and on the offer tiles, so the seller
    // could not take the form full-bleed (founder direction 2026-08-16).
    //
    // The DEFAULT is conditional on there BEING a border: full-bleed reads well on borderless elements, but a
    // bordered input or tile flush against the card's own border makes the two lines collide. The `min()`
    // saturates — ANY non-zero border width lands on 10px, a border of 0 collapses to 0 — so "only when we
    // have bordure" falls out of one expression, with no extra switch to maintain. 10px matches the list's
    // own 10px top padding, so the tiles sit in a visually even box (founder: the problem is left/right).
    const PADX = 'var(--cf-pad-inline,min(10px,calc(var(--cf-input-border-width,1.5px) * 20)))';
    // Halo colour follows the seller's focus colour when set (--cf-focus-halo, derived in builderCfOverrides);
    // unset it falls back to the theme tint — before this the halo was ALWAYS the tint, so "Couleur du focus"
    // only moved the 1.5px border and looked dead.
    const RING = `box-shadow:0 0 0 max(2px,var(--cf-focus-ring-width,3px)) var(--cf-focus-halo,${SOFT})`;
    // Floating-label scopes: FLT = the whole form in floating mode; FF = the fields whose label actually
    // floats (every field EXCEPT the phone — its +dial prefix box owns the label's spot, see below).
    const FLT = `${s}[data-cf-labels="floating"]`;
    const FF = `${FLT} .cf-field:not(:has(.cf-phone))`;
    // ── responsive reflow (mobile) ─────────────────────────────────────────────────────────────────────────
    // Emitted under BOTH `@media(max-width:749px)` (the LIVE storefront, viewport-based) AND
    // `@container aqx-page (max-width:749px)` (the BUILDER device-preview, whose frame changes the PAGE
    // container width, NOT the viewport — a plain @media never fires there). This mirror is the repo-wide
    // responsive convention (see sticky-atc / ProductLayout / media-gallery); WITHOUT the @container half the
    // buy-box never reflowed inside the builder's mobile frame — the bug this fixes.
    const mobile = 
    // the sticky mobile order-bar takes over from the inline CTA …
    `${s} .cf-sticky{position:sticky;inset-block-end:0;z-index:50;margin-block-start:14px;display:flex;` +
        `align-items:center;flex-wrap:wrap;gap:6px;padding:10px 16px 14px;` +
        // OPAQUE on purpose — do NOT bring back the frosted `backdrop-filter` here. A backdrop filter promotes
        // this bar to its own composited layer on iOS Safari, and that layer keeps showing its OLD pixels when
        // only the text inside it changes: picking another quantity tile repainted the summary but left the
        // sticky Total on the previous pack price until a scroll forced a recomposition (founder, 2026-08-18,
        // iPhone: 1 unit selected → summary 599,00 dh, sticky bar still 958,40 dh). No filter → no stale layer.
        `background:${CARD_BG};border-top:1px solid ${BOR};` +
        `box-shadow:0 -6px 18px -12px rgba(0,0,0,.35);}` +
        // … so hide the inline CTA on mobile (but NOT inside the popup modal, which owns its own CTA via
        // `:not(.cf-pop-portal)` — the embedded card only).
        `${s}:not(.cf-pop-portal) .cf-actions .cf-cta{display:none;}` +
        // …and COLLAPSE the actions block when that hidden CTA is all it holds. Its 16px/18px block padding was
        // still painting under an empty box, so the phone layout showed ~48px of dead space between the summary
        // and the sticky bar (founder, 2026-08-16: "so much padding here"). The `:has()` guard keeps the padding
        // the moment the block has anything real to show — the success card, an error message, the card element.
        `${s}:not(.cf-pop-portal) .cf-actions:not(:has(> :not(.cf-cta))){padding-block:0;}` +
        // (the horizontal insets are no longer overridden here — `--cf-pad-inline` drives them at every width)
        `${s} .cf-summary{margin-inline:${PADX};}`;
    // ── iOS : le champ ne doit pas faire zoomer la page ────────────────────────────────────────────────────
    // Safari et Chrome sur iPhone/iPad agrandissent TOUTE la page dès qu'un champ prend le focus si le texte
    // de ce champ fait moins de 16px — et ils ne reviennent jamais en arrière au blur. Nos champs sont à 14px
    // (15px en libellé flottant), donc la boîte d'achat sautait à la première lettre de « Nom complet »
    // (fondatrice, iPhone, 23/10/2026 : « quand je commence à écrire ça zoom »).
    //
    // Le SEUL levier que Safari respecte est la taille du texte elle-même : ≥16px sur le contrôle. `max()`
    // garde le choix du vendeur quand il a réglé plus grand (le curseur « Taille du texte » monte à 18px).
    // Le libellé flottant au repos suit la même taille — il tient la place du texte à venir ; une fois monté
    // il repasse à 10.5px par une règle plus spécifique, inchangée.
    //
    // Émis EN FIN de feuille pour passer devant la règle de base `.cf-input` et devant la règle du libellé
    // flottant (même spécificité, plus haut dans la feuille), sous le miroir @media + @container habituel —
    // plus `pointer:coarse`, car une tablette dépasse 749px et a exactement le même Safari.
    const noZoom = `${inp}{font-size:max(16px,var(--cf-input-font-size,14px));}` +
        `${FF} .cf-label{font-size:max(16px,var(--cf-input-font-size,15px));}`;
    // Popup → bottom-sheet on mobile (full-width, bottom-anchored, grab handle). Same @media + @container mirror.
    const popupMobile = `${s} .cf-pop-ov{align-items:flex-end;padding:0;}` +
        `${s} .cf-pop-panel{inline-size:100%;max-inline-size:100%;max-block-size:92vh;border-radius:22px 22px 0 0;` +
        `border-block-end:0;padding-block-start:28px;padding-block-end:max(24px,env(safe-area-inset-bottom));` +
        `animation:cf-pop-sheet .28s cubic-bezier(.2,.9,.3,1);}` +
        `${s} .cf-pop-panel::before{content:"";position:absolute;inset-block-start:9px;inset-inline-start:50%;` +
        `transform:translateX(-50%);inline-size:42px;block-size:4px;border-radius:999px;` +
        `background:color-mix(in srgb, var(--cf-text,#151515) 18%, transparent);}` +
        `${s} .cf-pop-close{inset-block-start:15px;}`;
    return (
    // ── the unified card ─────────────────────────────────────────────────────────────────────────────────
    `${s}{font-family:inherit;color:${TXT};background:${CARD_BG};padding:var(--cf-form-pad,0);` +
        `max-inline-size:var(--cf-form-max-width,none);position:relative;` +
        `border:var(--cf-form-border-width,1px) solid ${BOR};border-radius:calc(${RAD} + 4px);` +
        `box-shadow:var(--cf-form-shadow,0 1px 2px rgba(0,0,0,.04),0 12px 32px -16px rgba(0,0,0,.1));}` +
        // ── "Informations de livraison" section head ──
        `${s} .cf-fields-head{padding:15px ${PADX} 0;}` +
        `${s} .cf-heading{margin:0;font-size:var(--cf-heading-size,13.5px);font-weight:var(--cf-heading-weight,650);` +
        `color:var(--cf-heading-color,${TXT});text-align:var(--cf-heading-align,start);letter-spacing:-0.01em;}` +
        `${s} .cf-subhead{margin:2px 0 0;font-size:12px;color:${MUT};}` +
        // ── field grid ──
        `${s} .cf-grid{display:flex;flex-direction:column;gap:var(--cf-field-gap,13px);padding:14px ${PADX} 2px;}` +
        `${s} .cf-field{position:relative;}` +
        `${s} .cf-label{display:block;font-size:var(--cf-label-size,12.5px);font-weight:var(--cf-label-weight,600);` +
        `margin-block-end:6px;color:var(--cf-label-color,${TXT});}` +
        `${s} .cf-control{position:relative;}` +
        `${s} .cf-err{font-size:12px;color:${ERR};margin-block-start:5px;}` +
        // ── phone: a +dial prefix box + the input + a valid-check ──
        `${s} .cf-phone{display:flex;align-items:stretch;block-size:${IH};border:1.5px solid ${BOR};` +
        `border-radius:${IRAD};background:${FIELD};overflow:hidden;transition:border-color .15s ease,box-shadow .15s ease;}` +
        `${s} .cf-phone:focus-within{border-color:var(--cf-focus,${ACC});${RING};}` +
        `${s} .cf-phone .cf-input{border:0;border-radius:0;background:transparent;box-shadow:none;block-size:auto;` +
        `min-block-size:0;flex:1;min-inline-size:0;font-variant-numeric:tabular-nums;}` +
        `${s} .cf-phone .cf-input:focus{box-shadow:none;border:0;}` +
        `${s} .cf-phone-dial{display:flex;align-items:center;gap:5px;padding:0 11px;border-inline-end:1px solid ${BOR};` +
        `background:${SURF};font-size:13.5px;font-weight:600;color:${MUT};white-space:nowrap;}` +
        // The flag rides INSIDE the dial box and is decorative: `direction:ltr` keeps the two regional-indicator
        // letters in order on an Arabic (RTL) form, where the pair would otherwise swap into another country.
        `${s} .cf-phone-flag{direction:ltr;font-size:15px;line-height:1;}` +
        // …and so does the dial code itself: see the comment on `<span class="cf-phone-code">`.
        `${s} .cf-phone-code{${LTR_ISOLATE_CSS}}` +
        `${s} .cf-phone-ok{display:flex;align-items:center;padding-inline-end:11px;}` +
        // ── EN ARABE, LE NUMÉRO SE COLLE À LA BOÎTE « +212 » ──────────────────────────────────────────────
        // Fondatrice, 23/10/2026, capture du formulaire arabe : « le numero de tel doit etre ecrit en right
        // mais comme ça juste inline right ». Le champ peignait son numéro tout à GAUCHE, à 300 px de son
        // indicatif, alors que tous les autres champs de la colonne commencent à droite.
        //
        // Ce n'est pas notre feuille de style : les navigateurs Blink/WebKit imposent `direction:ltr` à
        // `input[type=tel|email|url|number]` dans leur feuille par défaut (mesuré : `getComputedStyle` rend
        // `ltr` sous un parent `dir="rtl"`). Avec `text-align:start`, « gauche » en découle.
        // On GARDE ce `direction:ltr` — un numéro s'écrit de gauche à droite, « 0634840799 » et jamais
        // l'inverse — et on ne déplace que l'alignement : le texte commence au bord droit, contre
        // l'indicatif, et continue vers la gauche. C'est exactement « inline right ».
        `${s}[dir="rtl"] .cf-phone .cf-input{text-align:right;}` +
        // ── input / textarea / select (scoped to .cf-input so the payment radios are NOT hit) ──
        `${inp}{inline-size:100%;box-sizing:border-box;font-family:inherit;font-size:var(--cf-input-font-size,14px);` +
        `min-block-size:${IH};padding:0 12px;border-radius:${IRAD};` +
        `border:var(--cf-input-border-width,1.5px) solid ${BOR};background:${FIELD};` +
        `color:var(--cf-input-text,${TXT});outline:none;transition:border-color .15s ease,box-shadow .15s ease;}` +
        `${s} input.cf-input,${s} select.cf-input{block-size:${IH};}` +
        // the delivery note / address is multi-line and grows with content (height:auto overrides the fixed above)
        `${s} textarea.cf-input{block-size:auto;min-block-size:0;padding:11px 12px;line-height:1.4;resize:none;}` +
        `${inp}::placeholder{color:var(--cf-input-placeholder,${MUT});opacity:.75;}` +
        // focus ring — a11y clamped (min 2px) so a seller can't style it invisible
        `${inp}:focus{outline:0;border-color:var(--cf-focus,${ACC});${RING};}` +
        // error state — red border + message (driven by the `data-err` attribute on the field)
        `${s} .cf-field[data-err] .cf-input,${s} .cf-field[data-err] .cf-phone{border-color:${ERR};}` +
        // ── summary card ──
        `${s} .cf-summary{margin:15px ${PADX} 0;background:${SURF};border-radius:${RAD};padding:13px 14px;` +
        `font-variant-numeric:tabular-nums;}` +
        `${s} .cf-sum-row{display:flex;justify-content:space-between;align-items:baseline;padding:3px 0;font-size:13.5px;}` +
        `${s} .cf-sum-label{color:${MUT};}` +
        `${s} .cf-sum-val{font-weight:600;}` +
        `${s} .cf-sum-was{text-decoration:line-through;color:${MUT};margin-inline-end:6px;font-size:12.5px;}` +
        // Accepted 1-Click Upsell lines (feature 0042 lot D): the same summary row, plus the × that removes one.
        // The × is hidden after a submit — see `submitSent` in the block.
        `${s} .cf-sum-ups .cf-sum-label{color:inherit;}` +
        `${s} .cf-sum-x{margin-inline-start:8px;border:0;background:none;color:${MUT};font-size:15px;` +
        `line-height:1;cursor:pointer;padding:0 2px;}` +
        `${s} .cf-sum-x:hover{color:inherit;}` +
        // Chain skeleton — three bars while the offers are fetched, so the shell the buyer asked for is on screen
        // instantly instead of a button that looks broken for three seconds.
        `${s} .cf-ups-load{display:flex;flex-direction:column;gap:12px;padding:8px 0;}` +
        `${s} .cf-ups-load span{display:block;block-size:14px;border-radius:7px;background:${SURF};` +
        `animation:cfUpsSkeleton 1.2s ease-in-out infinite;}` +
        `${s} .cf-ups-load span:nth-child(2){block-size:150px;}` +
        `${s} .cf-ups-load span:nth-child(3){inline-size:60%;align-self:center;}` +
        `@keyframes cfUpsSkeleton{0%,100%{opacity:1;}50%{opacity:.45;}}` +
        // ── promo code (feature 0014): quiet disclosure link → input row; applied row uses cf-sum-* ──
        `${s} .cf-promo{margin:12px ${PADX} 0;}` +
        `${s} .cf-promo-toggle{border:0;background:none;padding:0;font-size:13px;font-weight:600;` +
        `color:${ACC};cursor:pointer;font-family:inherit;text-decoration:underline;text-underline-offset:3px;}` +
        `${s} .cf-promo-row{display:flex;gap:8px;}` +
        `${s} .cf-promo-row .cf-input{flex:1 1 auto;min-inline-size:0;}` +
        `${s} .cf-promo-btn{block-size:${IH};padding:0 14px;border-radius:${IRAD};border:1.5px solid ${BOR};` +
        `background:${SURF};color:${TXT};font-weight:600;font-size:13.5px;cursor:pointer;font-family:inherit;` +
        `white-space:nowrap;flex-shrink:0;}` +
        `${s} .cf-promo-btn:disabled{opacity:.55;cursor:default;}` +
        `${s} .cf-promo-err{margin:6px 0 0;font-size:12.5px;color:${ERR};}` +
        `${s} .cf-sum-promo .cf-sum-val{color:${ACC};}` +
        `${s} .cf-sum-free{font-weight:600;color:${ACC};}` +
        `${s} .cf-sum-save{display:flex;justify-content:space-between;align-items:baseline;padding:3px 0;font-size:12.5px;` +
        `color:${ACC};font-weight:600;}` +
        `${s} .cf-sum-divider{border-top:1px dashed ${BOR};margin:8px 0;}` +
        `${s} .cf-sum-total{display:flex;justify-content:space-between;align-items:baseline;}` +
        `${s} .cf-sum-total-label{font-size:14px;font-weight:700;}` +
        `${s} .cf-sum-total-val{font-size:17px;font-weight:700;letter-spacing:-0.01em;}` +
        // ── the amounts read LEFT-TO-RIGHT, on every shop ──
        // Founder, 23/10/2026, Arabic shop: « je veux que la devise s'écrive left to right comme le fr ».
        // The formatter already writes « 899,00 dh »; the RTL paragraph is what splits it into a number run
        // and a Latin run and lays those two right-to-left, so the card painted « dh 899,00 ». `direction:ltr`
        // + an isolate pins the amount to a single left-to-right run and changes nothing around it — the
        // label keeps the right-hand side, the amount the left. Same idiom as `.cf-phone-flag` above.
        `${s} .cf-sum-val,${s} .cf-sum-total-val,${s} .cf-sticky-val,` +
        `${s} .cf-sum-save > span:last-child{${LTR_ISOLATE_CSS}}` +
        // ── actions area (selector + CTA + message + card element + trust) ──
        `${s} .cf-actions{padding:16px ${PADX} 18px;display:flex;flex-direction:column;gap:14px;}` +
        // ── two-line dark CTA ──
        // `container-type` — the CTA measures its OWN width so its label can never break in two
        // (founder, 22/09/2026; see blocks/_shared/oneLineCta.ts). Safe here: `inline-size:100%` means the
        // width comes from `.cf-actions`, never from the label, so inline-size containment changes nothing.
        `${s} .cf-cta{inline-size:100%;container-type:inline-size;block-size:var(--cf-btn-min-h,54px);border:none;cursor:pointer;` +
        `border-radius:var(--cf-btn-radius,${RAD});background:var(--cf-btn-bg,#1b1b1e);color:var(--cf-btn-text,#ffffff);` +
        `display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;font-family:inherit;` +
        `transition:opacity .15s ease,transform .1s ease;}` +
        `${s} .cf-cta:active{transform:scale(.985);}` +
        `${s} .cf-cta:disabled{opacity:.65;cursor:wait;}` +
        `${s} .cf-cta-main{font-size:var(--cf-btn-font-size,15px);font-weight:var(--cf-btn-weight,650);letter-spacing:-0.01em;}` +
        `${s} .cf-cta-sub{font-size:12px;opacity:.75;}` +
        `${s} .cf-msg-err{margin:0;font-size:13px;color:${ERR};}` +
        // ── success ("Commande reçue") card ──
        `${s} .cf-done{display:flex;gap:12px;align-items:flex-start;background:${SOFT};` +
        `border:1px solid color-mix(in srgb,${ACC} 30%,transparent);border-radius:${RAD};padding:14px;}` +
        `${s} .cf-done svg{flex:none;margin-block-start:1px;}` +
        `${s} .cf-done-title{font-size:14px;font-weight:650;}` +
        `${s} .cf-done-text{font-size:12.5px;color:${MUT};margin-block-start:2px;line-height:1.45;}` +
        // Le numéro rappelé dans la confirmation est, lui aussi, une chose qui se lit de gauche à droite :
        // « +212 634840799 ». Dans la phrase arabe qui l'entoure, l'indicatif et le numéro sont deux blocs de
        // chiffres que le paragraphe poserait de droite à gauche — le numéro d'abord, l'indicatif ensuite.
        `${s} .cf-done-phone{font-weight:650;color:${TXT};${LTR_ISOLATE_CSS}}` +
        `${s} .cf-done-edit{display:inline-block;font-size:12px;font-weight:650;color:${ACC};margin-block-start:7px;` +
        `cursor:pointer;background:none;border:0;padding:0;font-family:inherit;}` +
        // ── sticky mobile bar (its reflow rules live in `mobile`, emitted below under @media + @container) ──
        `${s} .cf-sticky{display:none;}` +
        // The Total takes its OWN row above the CTA (`flex:1 0 100%` on a wrapping bar), so the button spans the
        // full width instead of sharing the row with it (founder 2026-08-16). It also stops the long French
        // label ("Commander — Paiement à la livraison") from being squeezed into two clipped lines.
        `${s} .cf-sticky-total{flex:1 0 100%;display:flex;align-items:baseline;gap:8px;}` +
        `${s} .cf-sticky-label{font-size:11px;color:${MUT};}` +
        `${s} .cf-sticky-val{font-size:17px;font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:-0.01em;}` +
        // `--cf-cta-fs`: the sticky bar's own CTA size. It used to live on `.cf-cta-main` and win by
        // specificity; the one-line ceiling is an INLINE style now, so the size has to reach the label as a
        // variable or the bar would jump back to the seller's full-size text.
        `${s} .cf-sticky .cf-cta{flex:1 0 100%;min-inline-size:0;min-block-size:50px;block-size:auto;padding:7px 14px;--cf-cta-fs:14px;}` +
        `${s} .cf-sticky .cf-cta-main{font-size:14px;line-height:1.2;}` +
        `${s} .cf-sticky .cf-cta-sub{display:none;}` +
        `@media(max-width:749px){${mobile}}` +
        `@container aqx-page (max-width:749px){${mobile}}` +
        // ── popup: strip the section's own horizontal padding (the modal panel provides the inset) ──
        `${s}.cf-pop-portal .cf-fields-head,${s}.cf-pop-portal .cf-grid,${s}.cf-pop-portal .cf-actions,` +
        `${s}.cf-pop-portal .aqx-qo-head,${s}.cf-pop-portal .aqx-qo-list{padding-inline:0;}` +
        `${s}.cf-pop-portal .cf-summary{margin-inline:0;}` +
        `${s}.cf-pop-portal .cf-promo{margin-inline:0;}` +
        // ── label position: Floating (the Shrine signature) — the label sits inside the field and rises on
        // focus/fill (driven by :has on the field). Selects float the label permanently (no :placeholder-shown).
        // The PHONE field is excluded (`:not(:has(.cf-phone))`) and keeps its label above: the +dial prefix box
        // spans the field's full height at inline-start — exactly where a floating label sits — so the two always
        // overlapped. Fields are clamped to ≥52px here (`--cf-flt-h`): the risen label + 22/8 padding cannot fit
        // a shorter box, and the rest label centres on that SAME height for any `inputHeight` setting.
        // z-index:2 lifts the label ABOVE the opaque input — the input lives in a later-in-DOM `.cf-control`
        // sibling, so without this the floating label paints BEHIND the field and looks invisible at rest. ──
        `${FLT}{--cf-flt-h:max(${IH},52px);}` +
        `${FLT} .cf-input{min-block-size:var(--cf-flt-h);}` +
        `${FLT} input.cf-input,${FLT} select.cf-input{block-size:var(--cf-flt-h);}` +
        `${FLT} .cf-phone{block-size:var(--cf-flt-h);}` +
        // the phone's inner input fills the .cf-phone box (re-assert over the two sizing rules just above)
        `${FLT} .cf-phone .cf-input{block-size:auto;min-block-size:0;}` +
        `${FLT} .cf-promo-btn{block-size:var(--cf-flt-h);}` +
        `${FF} .cf-label{position:absolute;z-index:2;` +
        `inset-block-start:calc((var(--cf-flt-h) - 1.25em)/2);` +
        `inset-inline-start:calc(var(--cf-input-pad-x,12px) + 1px);margin:0;pointer-events:none;font-weight:500;` +
        `font-size:var(--cf-input-font-size,15px);letter-spacing:0;` +
        `color:var(--cf-input-placeholder,var(--cf-muted,#9b95ab));` +
        `transition:inset-block-start .12s ease,font-size .12s ease,color .12s ease;}` +
        `${FF} .cf-input::placeholder{opacity:0;}` +
        `${FF} .cf-input:focus,${FF} .cf-input:not(:placeholder-shown),` +
        `${FF} select.cf-input{padding-block:22px 8px;}` +
        `${FF}:has(.cf-input:focus) .cf-label,` +
        `${FF}:has(.cf-input:not(:placeholder-shown)) .cf-label,` +
        `${FF}:has(select.cf-input) .cf-label{inset-block-start:7px;font-size:10.5px;` +
        `letter-spacing:.04em;color:var(--cf-label-color,var(--cf-muted,#6f6a63));}` +
        // ── label position: Hidden — the placeholder is the label; the a11y label is kept but visually hidden ──
        `${s}[data-cf-labels="hidden"] .cf-label{position:absolute;inline-size:1px;block-size:1px;padding:0;` +
        `margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}` +
        // ── input style variants ─────────────────────────────────────────────
        `${s}[data-cf-input="filled"] .cf-input{background:var(--cf-input-fill,var(--cf-soft,#f4f2fb));border-color:transparent;}` +
        `${s}[data-cf-input="underlined"] .cf-input{background:transparent;border-width:0;` +
        `border-block-end:2px solid var(--cf-border,#e7e1d8);border-radius:0;padding-inline:2px;}` +
        `${s}[data-cf-input="soft"] .cf-input{background:var(--cf-input-fill,var(--cf-soft,#f4f2fb));border-color:transparent;` +
        `box-shadow:inset 2px 2px 5px rgba(0,0,0,.09), inset -2px -2px 6px rgba(255,255,255,.7);}` +
        `${s}[data-cf-input="glass"] .cf-input{background:color-mix(in srgb, var(--cf-surface,#ffffff) 14%, transparent);` +
        `-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);` +
        `border-color:color-mix(in srgb, var(--cf-text,#151515) 22%, transparent);}` +
        // ── les mêmes variantes sur l'ENVELOPPE du téléphone ────────────────
        // `.cf-phone` n'est pas un `.cf-input` : c'est un conteneur flex (boîte +212 · champ · coche) qui
        // peint lui-même la boîte visible, et il lisait `--cf-input-bg` / `--cf-surface` quoi qu'il arrive.
        // Conséquence : dès qu'un thème choisissait autre chose que `outlined`, TOUS les champs prenaient
        // `--cf-input-fill` sauf le téléphone, qui restait blanc au milieu de la colonne. Repéré à l'œil sur
        // le thème jouets (trois champs crème, un champ blanc), pas par un test.
        `${s}[data-cf-input="filled"] .cf-phone{background:var(--cf-input-fill,var(--cf-soft,#f4f2fb));border-color:transparent;}` +
        `${s}[data-cf-input="soft"] .cf-phone{background:var(--cf-input-fill,var(--cf-soft,#f4f2fb));border-color:transparent;` +
        `box-shadow:inset 2px 2px 5px rgba(0,0,0,.09), inset -2px -2px 6px rgba(255,255,255,.7);}` +
        `${s}[data-cf-input="underlined"] .cf-phone{background:transparent;border-width:0;` +
        `border-block-end:2px solid var(--cf-border,#e7e1d8);border-radius:0;}` +
        `${s}[data-cf-input="glass"] .cf-phone{background:color-mix(in srgb, var(--cf-surface,#ffffff) 14%, transparent);` +
        `-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);` +
        `border-color:color-mix(in srgb, var(--cf-text,#151515) 22%, transparent);}` +
        // La boîte « +212 » se distingue par un FILET, plus par un fond : sur une variante remplie, son
        // aplat gris cassait la continuité du champ.
        //
        // `underlined` MANQUAIT dans cette liste, et c'est le cas où ça se voit le plus : la variante
        // efface tous les fonds et tous les contours pour ne garder qu'un trait sous chaque champ — sauf
        // le « +212 », qui restait un rectangle gris plein au milieu d'une colonne de traits. Repéré à
        // l'œil sur le thème maison, pas par un test ; les trois autres variantes avaient déjà été
        // corrigées de la même façon, celle-ci avait été oubliée.
        `${s}[data-cf-input="filled"] .cf-phone-dial,${s}[data-cf-input="soft"] .cf-phone-dial,` +
        `${s}[data-cf-input="underlined"] .cf-phone-dial,` +
        `${s}[data-cf-input="glass"] .cf-phone-dial{background:transparent;}` +
        // ── popup layout (cfg.layout === 'popup') — trigger button → centred modal (desktop) / bottom sheet (mobile).
        // Portalled to document.body → viewport-fixed (escapes the builder device-frame's `contain:paint`, which
        // would otherwise trap + clip a position:fixed child inside the frame). ──────────────────────────────────
        `${s} .cf-pop-ov{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;` +
        `justify-content:center;padding:22px;background:rgba(15,12,25,.5);animation:cf-pop-fade .2s ease;}` +
        `${s} .cf-pop-panel{position:relative;inline-size:min(440px,100%);max-block-size:88vh;overflow-y:auto;` +
        `background:var(--cf-bg,#ffffff);border:1px solid color-mix(in srgb, var(--cf-border,#e7e1d8) 55%, transparent);` +
        `border-radius:20px;padding:30px 26px 26px;box-shadow:0 30px 80px -24px rgba(15,12,25,.5), 0 8px 22px -12px rgba(15,12,25,.28);` +
        `animation:cf-pop-rise .24s cubic-bezier(.2,.9,.3,1);}` +
        // the section heading, as the modal title, gets room for the ✕
        `${s} .cf-pop-panel .cf-heading{padding-inline-end:34px;}` +
        `${s} .cf-pop-close{position:absolute;inset-block-start:14px;inset-inline-end:14px;inline-size:34px;` +
        `block-size:34px;display:flex;align-items:center;justify-content:center;border:0;border-radius:50%;` +
        `background:color-mix(in srgb, var(--cf-text,#151515) 6%, transparent);font-size:21px;line-height:1;` +
        `cursor:pointer;color:var(--cf-muted,#6f6a63);transition:background .15s ease;}` +
        `${s} .cf-pop-close:hover{background:color-mix(in srgb, var(--cf-text,#151515) 12%, transparent);}` +
        // The portal re-wrapper carries the block class for scoped `--cf-*`/`.cf-*` styling — neutralise its card box.
        `${s}.cf-pop-portal{background:none;border:0;padding:0;box-shadow:none;max-inline-size:none;}` +
        // Builder editor: no dark scrim + click-through, so the centred panel floats over the canvas while the
        // rail/inspector stay usable and the click still reaches the trigger to select the block.
        `${s} .cf-pop-ov[data-editor]{background:transparent;pointer-events:none;}` +
        `${s} .cf-pop-ov[data-editor] .cf-pop-panel{pointer-events:auto;box-shadow:0 30px 80px -20px rgba(15,12,25,.55);}` +
        // ── MOBILE → bottom sheet: full-width, anchored to the bottom, rounded top, grab handle, slides up.
        // Same @media (live) + @container (builder device-preview) mirror as the embedded card above. ──
        `@media(max-width:749px){${popupMobile}}` +
        `@container aqx-page (max-width:749px){${popupMobile}}` +
        `@keyframes cf-pop-fade{from{opacity:0}to{opacity:1}}` +
        `@keyframes cf-pop-rise{from{opacity:0;transform:translateY(10px) scale(.97)}to{opacity:1;transform:none}}` +
        `@keyframes cf-pop-sheet{from{transform:translateY(100%)}to{transform:none}}` +
        `@media(prefers-reduced-motion:reduce){${s} .cf-pop-ov,${s} .cf-pop-panel{animation:none;}}` +
        // ── iOS : pas de zoom au focus (voir `noZoom` plus haut) ──
        `@media(max-width:749px){${noZoom}}` +
        `@container aqx-page (max-width:749px){${noZoom}}` +
        `@media(pointer:coarse){${noZoom}}`);
}
/** A number setting → `${n}px`, or undefined when unset / not a finite number (so the mapper skips it). */
function px(v, min = -Infinity) {
    return typeof v === 'number' && Number.isFinite(v) && v >= min ? `${v}px` : undefined;
}
/** A non-empty string setting, else undefined. */
function pick(v) {
    return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
}
/** A ColorValue setting → resolved CSS colour, else undefined. */
function col(v) {
    return resolveColor(v);
}
/**
 * Turn the block's inspector settings into a `--cf-*` override map, layered OVER the dashboard theme. Only
 * SET fields emit a var (unset → falls through to the dashboard theme / literal fallback). Presets (palette,
 * corner shape) are expanded FIRST so a granular field set afterwards wins — e.g. pick "Rubin" then override
 * just the accent. Colours go through {@link resolveColor}; a `gradient` button value is already a raw
 * `linear-gradient()` string and drops straight into `--cf-btn-bg`.
 */
export function builderCfOverrides(bs) {
    const out = {};
    const set = (k, v) => {
        if (v !== undefined)
            out[k] = v;
    };
    // 1) Palette preset → colour vars (expanded first, so granular colours below can override).
    const pal = pick(bs.palette);
    if (pal) {
        const t = findTemplate(pal);
        if (t) {
            for (const [k, v] of Object.entries(t.patch)) {
                const varName = COLOR_VAR[k];
                if (varName && typeof v === 'string')
                    set(varName, v);
            }
        }
    }
    // 2) Corner-shape preset → the three radius vars.
    const shape = pick(bs.cornerShape);
    if (shape) {
        const t = findTemplate(shape);
        if (t) {
            for (const [k, v] of Object.entries(t.patch)) {
                const varName = RADIUS_VAR[k];
                if (varName && typeof v === 'number')
                    set(varName, `${v}px`);
            }
        }
    }
    // 3) Granular controls (win over the presets above for the same var).
    // Container / card
    set('--cf-form-bg', col(bs.formBg));
    set('--cf-form-pad', px(bs.formPad, 0));
    set('--cf-form-max-width', px(bs.formMaxWidth, 1)); // 0/unset = no cap (stays `none`)
    set('--cf-form-border-width', px(bs.formBorderWidth, 0));
    set('--cf-form-shadow', pick(bs.formShadow) ? SHADOW[String(bs.formShadow)] : undefined);
    // Inputs
    set('--cf-accent', col(bs.accent));
    set('--cf-input-height', px(bs.inputHeight, 1));
    set('--cf-input-radius', px(bs.inputRadius, 0));
    set('--cf-input-fill', col(bs.inputFill));
    set('--cf-input-font-size', px(bs.inputFontSize, 1));
    set('--cf-input-border-width', px(bs.inputBorderWidth, 0));
    set('--cf-input-text', col(bs.inputText));
    set('--cf-input-placeholder', col(bs.placeholderColor));
    // Labels
    set('--cf-label-color', col(bs.labelColor));
    set('--cf-label-size', px(bs.labelSize, 1));
    set('--cf-label-weight', pick(bs.labelWeight));
    // Heading
    set('--cf-heading-size', px(bs.headingSize, 1));
    set('--cf-heading-weight', pick(bs.headingWeight));
    set('--cf-heading-color', col(bs.headingColor));
    set('--cf-heading-align', pick(bs.headingAlign));
    // Button
    set('--cf-btn-bg', col(bs.btnBg));
    set('--cf-btn-text', col(bs.btnText));
    set('--cf-btn-radius', px(bs.btnRadius, 0));
    set('--cf-btn-min-h', px(bs.btnMinH, 1));
    set('--cf-btn-font-size', px(bs.btnFontSize, 1));
    set('--cf-btn-weight', pick(bs.btnWeight));
    if (bs.btnFullWidth === false)
        set('--cf-btn-width', 'fit-content');
    // Focus — the chosen colour drives BOTH the focused border and the halo (a translucent mix of it).
    const focus = col(bs.focusColor);
    set('--cf-focus', focus);
    if (focus)
        set('--cf-focus-halo', `color-mix(in srgb, ${focus} 30%, transparent)`);
    set('--cf-focus-ring-width', px(bs.focusRingWidth, 0));
    // Offer tiles (quantity offers) — design is builder-owned; the COD app only authors the VALUES
    // (tiers, quantities, discounts, plaque text). See OfferTiles.tsx for the consuming vars.
    set('--qo-bg', col(bs.offerTileBg));
    set('--qo-border', col(bs.offerTileBorder));
    set('--qo-price', col(bs.offerPriceColor));
    set('--qo-plaque-bg', col(bs.offerPlaqueBg));
    set('--qo-plaque-text', col(bs.offerPlaqueText));
    set('--qo-text-size', px(bs.offerTextSize, 1));
    set('--qo-price-size', px(bs.offerPriceSize, 1));
    set('--qo-text-weight', pick(bs.offerTextWeight));
    set('--qo-price-weight', pick(bs.offerPriceWeight));
    set('--qo-was-weight', pick(bs.offerWasWeight));
    set('--cf-pad-inline', px(bs.formPadInline, 0));
    set('--qo-list-gap', px(bs.offerListGap, 0));
    set('--qo-list-pad-inline', px(bs.offerListPadInline, 0));
    set('--qo-list-pad-top', px(bs.offerListPadTop, 0));
    set('--qo-plaque-size', px(bs.offerPlaqueSize, 1));
    set('--qo-plaque-weight', pick(bs.offerPlaqueWeight));
    set('--qo-plaque-radius', px(bs.offerPlaqueRadius, 0));
    set('--qo-tile-radius', px(bs.offerTileRadius, 0));
    set('--qo-border-width', px(bs.offerBorderWidth, 0));
    set('--qo-sel-bg', col(bs.offerSelectedBg));
    set('--qo-sel-border', col(bs.offerSelectedBorder));
    set('--qo-tile-pad', px(bs.offerTilePad, 0));
    set('--qo-thumb-size', px(bs.offerImageSize, 0));
    if (bs.offerImageSize === 0 || bs.offerShowImage === false)
        set('--qo-thumb-display', 'none');
    if (bs.offerHideBadge === true)
        set('--qo-badge-display', 'none');
    set('--qo-plaque-pad-y', px(bs.offerPlaquePadY, 0));
    return out;
}
// ── inspector schema fields ────────────────────────────────────────────────────────────────────────
const paletteOptions = [
    { value: '', label: '— Default (theme)' },
    ...THEME_TEMPLATES.map((t) => ({ value: t.id, label: t.name })),
];
const shapeOptions = [
    { value: '', label: '— Default' },
    ...SHAPE_TEMPLATES.map((t) => ({ value: t.id, label: t.name })),
];
const WEIGHT_OPTIONS = [
    { value: '400', label: 'Normal' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'Extra-bold' },
];
/**
 * The grouped design controls pushed into `codFormBlockSchema.fields`. Rendered immediately by the builder
 * inspector (group headers + conditional visibility work out of the box). Ranges/colours carry NO `default`
 * so an untouched field emits nothing and inherits the dashboard theme; selects default to the neutral option.
 */
export const codDesignFields = [
    // Style
    {
        id: 'inputVariant',
        type: 'select',
        label: 'Field style',
        group: 'Style',
        default: 'outlined',
        info: 'Outlined = border · Filled = solid background · Underlined = bottom line · Soft = raised · Glass = frosted.',
        options: [
            { value: 'outlined', label: 'Outlined' },
            { value: 'filled', label: 'Filled' },
            { value: 'underlined', label: 'Underlined' },
            { value: 'soft', label: 'Soft' },
            { value: 'glass', label: 'Glass' },
        ],
    },
    {
        id: 'cornerShape',
        type: 'select',
        label: 'Corner shape',
        group: 'Style',
        default: '',
        options: shapeOptions,
    },
    {
        id: 'palette',
        type: 'select',
        label: 'Palette',
        group: 'Style',
        default: '',
        options: paletteOptions,
    },
    { id: 'accent', type: 'color', label: 'Accent (focus, selection)', group: 'Style' },
    // Carte
    { id: 'formBg', type: 'color', label: 'Form background', group: 'Card' },
    {
        id: 'formPad',
        type: 'range',
        label: 'Inner padding',
        group: 'Card',
        min: 0,
        max: 40,
        step: 1,
        unit: 'px',
    },
    {
        id: 'formMaxWidth',
        type: 'range',
        label: 'Max width (0 = full)',
        group: 'Card',
        min: 0,
        max: 720,
        step: 10,
        unit: 'px',
    },
    {
        id: 'formShadow',
        type: 'select',
        label: 'Shadow',
        group: 'Card',
        // `soft` is the validated Claude-Design card shadow — the out-of-the-box unified-card look.
        default: 'soft',
        options: [
            { value: 'none', label: 'None' },
            { value: 'soft', label: 'Soft' },
            { value: 'medium', label: 'Medium' },
            { value: 'floating', label: 'Floating' },
        ],
    },
    {
        id: 'formBorderWidth',
        type: 'range',
        label: 'Card border thickness',
        group: 'Card',
        min: 0,
        max: 3,
        step: 1,
        unit: 'px',
    },
    // Champs
    {
        id: 'inputHeight',
        type: 'range',
        label: 'Field height',
        group: 'Fields',
        min: 36,
        max: 64,
        step: 1,
        unit: 'px',
    },
    {
        id: 'inputRadius',
        type: 'range',
        label: 'Field corner radius',
        group: 'Fields',
        min: 0,
        max: 32,
        step: 1,
        unit: 'px',
    },
    { id: 'inputFill', type: 'color', label: 'Field background (filled/soft)', group: 'Fields' },
    { id: 'inputText', type: 'color', label: 'Typed text', group: 'Fields' },
    { id: 'placeholderColor', type: 'color', label: 'Placeholder', group: 'Fields' },
    {
        id: 'inputFontSize',
        type: 'range',
        label: 'Text size',
        group: 'Fields',
        min: 13,
        max: 18,
        step: 1,
        unit: 'px',
    },
    {
        id: 'inputBorderWidth',
        type: 'range',
        label: 'Border thickness',
        group: 'Fields',
        min: 0,
        max: 3,
        step: 1,
        unit: 'px',
    },
    // Labels
    {
        id: 'labelStyle',
        type: 'select',
        label: 'Label position',
        group: 'Labels',
        default: 'above',
        info: 'Floating = Shrine style: the label sits inside the field and rises on focus.',
        options: [
            { value: 'above', label: 'Above' },
            { value: 'floating', label: 'Floating (Shrine)' },
            { value: 'hidden', label: 'Hidden (placeholder only)' },
        ],
    },
    { id: 'labelColor', type: 'color', label: 'Label color', group: 'Labels' },
    {
        id: 'labelSize',
        type: 'range',
        label: 'Label size',
        group: 'Labels',
        min: 10,
        max: 16,
        step: 1,
        unit: 'px',
    },
    {
        id: 'labelWeight',
        type: 'select',
        label: 'Label weight',
        group: 'Labels',
        default: '600',
        options: WEIGHT_OPTIONS,
    },
    // Titre
    {
        id: 'headingSize',
        type: 'range',
        label: 'Title size',
        group: 'Title',
        min: 13,
        max: 28,
        step: 1,
        unit: 'px',
    },
    {
        id: 'headingWeight',
        type: 'select',
        label: 'Title weight',
        group: 'Title',
        default: '700',
        options: WEIGHT_OPTIONS,
    },
    { id: 'headingColor', type: 'color', label: 'Title color', group: 'Title' },
    {
        id: 'headingAlign',
        type: 'select',
        label: 'Title alignment',
        group: 'Title',
        default: 'start',
        options: [
            { value: 'start', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'end', label: 'Right' },
        ],
    },
    // Bouton
    { id: 'btnBg', type: 'color', label: 'Button background', group: 'Button' },
    { id: 'btnText', type: 'color', label: 'Button text', group: 'Button' },
    {
        // Founder, 2026-09-21: the order button's text size and weight, per page. Unset = what the button has
        // always painted (15 px / 650, see `.cf-cta-main`), so adding the two controls moves no published page:
        // the range keeps NO stored default (`inheritLabel`) and the select's '' emits no var.
        id: 'btnFontSize',
        type: 'range',
        label: 'Button text size',
        group: 'Button',
        info: 'On « Default », the button shrinks its text rather than break it in two. Set a size and yours is painted, exactly as typed.',
        inheritLabel: 'Default (15 px)',
        default: 15,
        min: 12,
        max: 24,
        step: 1,
        unit: 'px',
    },
    {
        id: 'btnWeight',
        type: 'select',
        label: 'Button text weight',
        group: 'Button',
        default: '',
        options: [{ value: '', label: '— Default (semibold)' }, ...WEIGHT_OPTIONS],
    },
    {
        id: 'btnRadius',
        type: 'range',
        label: 'Button corner radius',
        group: 'Button',
        min: 0,
        max: 32,
        step: 1,
        unit: 'px',
    },
    {
        id: 'btnMinH',
        type: 'range',
        label: 'Button height',
        group: 'Button',
        min: 40,
        max: 64,
        step: 1,
        unit: 'px',
    },
    {
        id: 'btnFullWidth',
        type: 'checkbox',
        label: 'Full-width button',
        group: 'Button',
        default: true,
    },
    {
        id: 'btnUppercase',
        type: 'checkbox',
        label: 'Uppercase button',
        group: 'Button',
        default: false,
    },
    // Offres de quantité — le design des tuiles d'offres vit ICI (le builder) ; les valeurs (paliers,
    // quantités, remises, texte de la plaque) restent dans l'app COD (Offres de quantité).
    { id: 'offerTileBg', type: 'color', label: 'Tile background', group: 'Offers' },
    { id: 'offerTileBorder', type: 'color', label: 'Tile border', group: 'Offers' },
    { id: 'offerPriceColor', type: 'color', label: 'Price color', group: 'Offers' },
    {
        id: 'offerPlaqueBg',
        type: 'color',
        label: 'Plaque background',
        group: 'Offers',
        info: 'The plaque only shows on tiers that carry a "Plaque text" (COD app → Quantity offers). Without text, the colour appears nowhere.',
    },
    {
        id: 'offerPlaqueText',
        type: 'color',
        label: 'Plaque text',
        group: 'Offers',
        info: 'Same rule: without a "Plaque text" on a tier, no plaque is rendered.',
    },
    {
        id: 'offerTextSize',
        type: 'range',
        label: 'Text size',
        group: 'Offers',
        min: 12,
        max: 20,
        step: 0.5,
        unit: 'px',
    },
    {
        id: 'offerPriceSize',
        type: 'range',
        label: 'Price size',
        group: 'Offers',
        min: 12,
        max: 24,
        step: 0.5,
        unit: 'px',
    },
    {
        id: 'offerTextWeight',
        type: 'select',
        label: 'Text weight',
        group: 'Offers',
        default: '',
        options: [{ value: '', label: '— Default (bold)' }, ...WEIGHT_OPTIONS],
    },
    {
        id: 'offerPriceWeight',
        type: 'select',
        label: 'Price weight',
        group: 'Offers',
        default: '',
        options: [{ value: '', label: '— Default (bold)' }, ...WEIGHT_OPTIONS],
    },
    {
        id: 'offerWasWeight',
        type: 'select',
        label: 'Compare-price weight',
        group: 'Offers',
        default: '',
        options: [{ value: '', label: '— Default (normal)' }, ...WEIGHT_OPTIONS],
    },
    {
        id: 'offerTileRadius',
        type: 'range',
        label: 'Tile corner radius',
        group: 'Offers',
        min: 0,
        max: 28,
        step: 1,
        unit: 'px',
    },
    {
        id: 'offerBorderWidth',
        type: 'range',
        label: 'Border thickness',
        group: 'Offers',
        min: 0,
        max: 4,
        step: 0.5,
        unit: 'px',
    },
    // Selected tier — until now this was hard-wired to the block's Accent colour, with no way to style the
    // chosen tile on its own (founder, 2026-08-16: "où on peut la configurer, je la vois pas").
    {
        id: 'offerSelectedBg',
        type: 'color',
        label: 'Selected tile background',
        group: 'Offers',
        info: 'Unset = the form accent tint.',
    },
    {
        id: 'offerSelectedBorder',
        type: 'color',
        label: 'Selected tile border',
        group: 'Offers',
        info: 'Also drives the ring around the chosen tile. Unset = the form accent.',
    },
    {
        id: 'offerTilePad',
        type: 'range',
        label: 'Tile padding',
        group: 'Offers',
        min: 6,
        max: 28,
        step: 1,
        unit: 'px',
    },
    {
        id: 'offerShowImage',
        type: 'checkbox',
        label: 'Show product image',
        group: 'Offers',
        default: true,
        info: 'Untick for tiles without the product thumbnail.',
    },
    {
        id: 'offerImageSize',
        type: 'range',
        label: 'Image size',
        group: 'Offers',
        visibleWhen: { field: 'offerShowImage', equals: true },
        min: 0,
        max: 88,
        step: 2,
        unit: 'px',
        info: '0 hides the product thumbnail.',
    },
    {
        id: 'offerPlaquePadY',
        type: 'range',
        label: 'Plaque padding (top/bottom)',
        group: 'Offers',
        min: 0,
        max: 14,
        step: 1,
        unit: 'px',
    },
    {
        id: 'formPadInline',
        type: 'range',
        label: 'Form side padding',
        group: 'Card',
        min: 0,
        max: 32,
        step: 1,
        unit: 'px',
        info: 'Inset of the tiles, fields and summary from the card edges. 0 = full width.',
    },
    {
        id: 'offerListGap',
        type: 'range',
        label: 'Space between offers',
        group: 'Offers',
        min: 0,
        max: 24,
        step: 1,
        unit: 'px',
    },
    {
        id: 'offerListPadTop',
        type: 'range',
        label: 'Space above the offers',
        group: 'Offers',
        min: 0,
        max: 32,
        step: 1,
        unit: 'px',
    },
    {
        id: 'offerListPadInline',
        type: 'range',
        label: 'Offers side padding',
        group: 'Offers',
        min: 0,
        max: 32,
        step: 1,
        unit: 'px',
        info: '20 px aligns the tiles with the form fields. Below that, they spill towards the card edges.',
    },
    {
        id: 'offerPlaqueSize',
        type: 'range',
        label: 'Plaque text size',
        group: 'Offers',
        min: 8,
        max: 20,
        step: 0.5,
        unit: 'px',
    },
    {
        id: 'offerPlaqueWeight',
        type: 'select',
        label: 'Plaque text weight',
        group: 'Offers',
        default: '',
        options: [{ value: '', label: '— Default (bold)' }, ...WEIGHT_OPTIONS],
    },
    {
        id: 'offerPlaqueRadius',
        type: 'range',
        label: 'Plaque corner radius',
        group: 'Offers',
        min: 0,
        max: 20,
        step: 1,
        unit: 'px',
    },
    {
        id: 'offerHideBadge',
        type: 'checkbox',
        label: 'Hide the automatic saving line',
        group: 'Offers',
        default: false,
        info: 'The line under the tier label (“You save 40 dh”) when no badge is written. Tick when the saving is already in the plaque.',
    },
    // Focus
    { id: 'focusColor', type: 'color', label: 'Focus color', group: 'Focus' },
    {
        id: 'focusRingWidth',
        type: 'range',
        label: 'Focus ring thickness',
        group: 'Focus',
        min: 0,
        max: 6,
        step: 1,
        unit: 'px',
    },
];
//# sourceMappingURL=design.js.map